import { Button } from '@/components'
import { DownloadFormat, Process } from '@/store'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuItem,
} from '@/components'
import { createColumnHelper } from '@tanstack/react-table'
import {
  TrashIcon,
  DownloadIcon,
  RefreshCwIcon,
  CheckIcon,
  EyeIcon,
} from 'lucide-react'
import { createTranslator, Messages, useTranslations } from 'next-intl'

const columnHelper = createColumnHelper<Process & { actions?: unknown }>()

export enum HistoryEvents {
  DOWNLOAD = 'download',
  DELETE = 'delete',
  SHOW = 'show',
}

export const getColumns = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: ReturnType<typeof createTranslator<Messages, any>>,
) => {
  return {
    id: columnHelper.accessor('id', {
      header: '#',
      enableHiding: true,
      size: 100,
      cell: ({ cell, table: { emitCustomEvent } }) => {
        const val = cell.getValue()
        return (
          <span
            title={val}
            className="cursor-pointer"
            onClick={() =>
              emitCustomEvent({
                eventName: HistoryEvents.SHOW,
                eventParams: {
                  id: cell.getValue(),
                },
              })
            }
          >{`${val.substring(0, 8)}.....${val.substring(val.length - 9)}`}</span>
        )
      },
    }),
    url: columnHelper.accessor('url', {
      header: t('table.url'),
    }),
    status: columnHelper.accessor('isFinished', {
      header: t('table.status'),
      cell: ({ cell }) =>
        cell.getValue() === true ? (
          <span className="flex gap-3 items-center">
            <CheckIcon className="w-4 h-4 text-green-500" />
            {t('status.finished')}
          </span>
        ) : (
          <span className="flex gap-3 items-center">
            <RefreshCwIcon className="animate-spin w-3 h-3" />
            {t('status.inProgress')}
          </span>
        ),
    }),
    pageCount: columnHelper.accessor('pageCount', {
      header: t('table.pageCount'),
      cell: ({ cell }) => cell.getValue() ?? 0,
    }),
    actions: columnHelper.accessor('actions', {
      cell: ({ row: { original }, table: { emitCustomEvent } }) => {
        return (
          <Actions
            id={original.id}
            emitEvent={(event, params) => {
              emitCustomEvent({
                eventName: event,
                eventParams: params,
              })
            }}
          />
        )
      },
      header: '',
      size: 80,
    }),
  }
}

const Actions = ({
  id,
  emitEvent,
}: {
  id: string
  emitEvent?: (
    action: HistoryEvents,
    params: { id: string; format?: DownloadFormat },
  ) => void
}) => {
  const t = useTranslations('history')

  return (
    <div className="flex gap-3 justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="text-white/60">
          <Button variant="ghost" size="sm">
            <DownloadIcon /> {t('actions.download')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-24 rounded-lg bg-glow/5 border-white/10 text-white/60"
          align="end"
        >
          <DropdownMenuLabel>{t('actions.formats')}</DropdownMenuLabel>
          <DropdownMenuGroup>
            <DropdownMenuItem
              className="text-left"
              onClick={() =>
                emitEvent?.(HistoryEvents.DOWNLOAD, {
                  id,
                  format: DownloadFormat.CSV,
                })
              }
            >
              <DownloadIcon className="w-4 h-4" /> {DownloadFormat.CSV}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                emitEvent?.(HistoryEvents.DOWNLOAD, {
                  id,
                  format: DownloadFormat.HTML,
                })
              }
              className="text-left"
            >
              <DownloadIcon className="w-4 h-4" /> {DownloadFormat.HTML}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-left"
              onClick={() =>
                emitEvent?.(HistoryEvents.DOWNLOAD, {
                  id,
                  format: DownloadFormat.JSON,
                })
              }
            >
              <DownloadIcon className="w-4 h-4" /> {DownloadFormat.JSON}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-left"
              onClick={() =>
                emitEvent?.(HistoryEvents.DOWNLOAD, {
                  id,
                  format: DownloadFormat.TXT,
                })
              }
            >
              <DownloadIcon className="w-4 h-4" /> {DownloadFormat.TXT}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-left"
              onClick={() =>
                emitEvent?.(HistoryEvents.DOWNLOAD, {
                  id,
                  format: DownloadFormat.XLSX,
                })
              }
            >
              <DownloadIcon className="w-4 h-4" /> {DownloadFormat.XLSX}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-left"
              onClick={() =>
                emitEvent?.(HistoryEvents.DOWNLOAD, {
                  id,
                  format: DownloadFormat.XML,
                })
              }
            >
              <DownloadIcon className="w-4 h-4" /> {DownloadFormat.XML}
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        size="sm"
        variant="ghost"
        onClick={() => emitEvent?.(HistoryEvents.SHOW, { id })}
      >
        <EyeIcon />
      </Button>

      <Button
        size="sm"
        variant="ghost"
        className="bg-red-500"
        onClick={() => emitEvent?.(HistoryEvents.DELETE, { id })}
      >
        <TrashIcon />
        {t('actions.delete')}
      </Button>
    </div>
  )
}
